import { SMP_PAYMENT_METHODS_PATH } from '@/constants/paths';

export type PaymentUpdateOutcome = 'success' | 'unchanged';

/**
 * Values of Loop's `cardAddEvent` that mean the card was added. Observed on
 * staging (2026-10-12): Loop returns `cardAddEvent=card_add_success` together
 * with `customerPaymentMethodId=<new id>`. Anything not in this list is
 * treated as "no change". Compared lowercase. Single place to update if Loop
 * adds further success values.
 */
export const CARD_ADD_SUCCESS_EVENTS: readonly string[] = ['card_add_success'];

export const CARD_ADD_EVENT_PARAM = 'cardAddEvent';
export const CUSTOMER_PAYMENT_METHOD_ID_PARAM = 'customerPaymentMethodId';

/** The slice of `useIframeModeStore` state the return URL needs. */
export interface IframeReturnContext {
  isIframeMode: boolean;
  customerId: string;
  os: string;
  hasRecommendation: boolean;
  hasPersonalizedSupplementRecommendations: boolean;
  isEligibleForSupplements: boolean;
}

/**
 * Absolute URL Loop sends the browser back to after the card update. It must
 * let the page re-resolve itself on a cold load:
 *   - always: `subscriptionId` (useSMPData reads it from the URL)
 *   - webview only: `in_iframe=true`, `customerId`, `os`, and the boolean
 *     flags when true (absent reads as false in getIframeModeParams)
 * It never carries the auth token: useInitialization persisted it at boot,
 * and it must not transit a third-party page.
 */
export const buildReturnUrl = (
  location: Pick<Location, 'origin'>,
  ctx: { subscriptionId: string; iframe: IframeReturnContext },
): string => {
  const params = new URLSearchParams({ subscriptionId: ctx.subscriptionId });
  const { iframe } = ctx;
  if (iframe.isIframeMode) {
    params.set('in_iframe', 'true');
    if (iframe.customerId) params.set('customerId', iframe.customerId);
    if (iframe.os) params.set('os', iframe.os);
    if (iframe.hasRecommendation) params.set('hasRecommendation', 'true');
    if (iframe.hasPersonalizedSupplementRecommendations) {
      params.set('hasPersonalizedSupplementRecommendations', 'true');
    }
    if (iframe.isEligibleForSupplements) params.set('isEligibleForSupplements', 'true');
  }
  return `${location.origin}${SMP_PAYMENT_METHODS_PATH}?${params.toString()}`;
};

/**
 * Loop reads `redirectUrl` from its own query string. The backend URL already
 * carries `?sessionToken=…`, so join with `&`; fall back to `?` defensively.
 */
export const appendRedirectUrl = (updatePaymentUrl: string, returnTo: string): string => {
  const separator = updatePaymentUrl.includes('?') ? '&' : '?';
  return `${updatePaymentUrl}${separator}redirectUrl=${encodeURIComponent(returnTo)}`;
};

/**
 * null when Loop's param is absent; otherwise success vs "no change".
 * Accepts a query string or react-router's `URLSearchParams` directly.
 */
export const readReturnOutcome = (
  search: string | URLSearchParams,
): PaymentUpdateOutcome | null => {
  const value = new URLSearchParams(search).get(CARD_ADD_EVENT_PARAM);
  if (value === null) return null;
  return CARD_ADD_SUCCESS_EVENTS.includes(value.toLowerCase()) ? 'success' : 'unchanged';
};

/** Copy of `params` without Loop's return params (so a refresh stays quiet). */
export const stripReturnParams = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(CARD_ADD_EVENT_PARAM);
  next.delete(CUSTOMER_PAYMENT_METHOD_ID_PARAM);
  return next;
};
